  import React, { useEffect, useMemo } from "react";
  import { useDispatch, useSelector } from 'react-redux';

  import ManifestsState from '../../../slices/manifest/ManifestsState';
  import { StopStatus, StopType, PickupStatus } from "../../../app/data/common/route";
  import { reset_manifest_map_data, getManifestMapData, manifestsSelector, stopsInfoSelector } from '../../../slices/manifest/manifestSlice';
  import { IState } from "../../../slices";
  import { getStopFillColor } from "../../../services/map";

  import XGSErrorMessage from '../../../ui-components/error-message/errorMessage';
  import XGSMarkerTooltip from "../../../ui-components/map/marker-tooltip/markerTooltip";
  import Loading from "../../../ui-components/loading/loading";
  import MapHeader from "../../../ui-components/map/map-header/mapHeader";
  import StopsLegend from "../../../ui-components/map/stops-legend/stopsLegend";
  import StopMarkerInfo from "../../../ui-components/map/stop-marker-info/stopMarkerInfo";

  import "../../../ui-components/map/map.scss";
  import { formatAddress } from "../../../services/common/address";
  import XGSLeafletMap from "ui-components/map/leaflet/XGSLeafletMap";
  import { LeafletMarkerKind, LeafletMarkerModel, LeafletPolylineModel } from "ui-components/map/leaflet/xgsLeafletMap.types";
  import { decodeGooglePolylineToLatLng } from "ui-components/map/leaflet/decodeGooglePolyline";

  export interface StopMarker {
    id: string,
    label: string;
    description?: string;
    lat: number;
    lng: number;
  }

  export interface ManifestMapProps {
    manifestNumber?: string;
    stopStatuses?: StopStatus[];
  };

  const ManifestMap: React.FC<ManifestMapProps> = ({ manifestNumber, stopStatuses }) => {

    const dispatch = useDispatch();
    const manifestsState: ManifestsState = useSelector(manifestsSelector);
    const stopsInfo = useSelector((state: IState) => stopsInfoSelector(state, manifestNumber));

    const assignedPickups = useMemo(() => {
      return (manifestNumber && manifestsState.manifestsPickups[manifestNumber]) || [];
    }, [manifestNumber, manifestsState.manifestsPickups]);
    
    const stops = useMemo(() => {
      return manifestsState.manifestMapData?.markers || [];
    }, [manifestsState.manifestMapData?.markers]);

    useEffect(() => {
      if (!manifestNumber) return;

      dispatch(getManifestMapData(manifestNumber));

      return () => { dispatch(reset_manifest_map_data()) };
      // eslint-disable-next-line
    }, [manifestNumber]);

    const { manifestMapData } = manifestsState;

    const polyline = manifestMapData?.polyline;

    const isError = manifestsState.map_fetch_was_failed;
    const isLoading = !manifestsState.map_fetch_was_failed && !manifestMapData;

    const markers: LeafletMarkerModel[] = useMemo(() => [

      ...stops.map((stop: StopMarker) => {
        const type = stop.label === "SC" ? StopType.SERVICE_CENTER : StopType.STOP;
        const status = type === StopType.STOP ? stopStatuses?.[+stop.label] : null;

        return {
          id: stop.id,
          kind: type === StopType.SERVICE_CENTER ? "service" : "delivery",
          lat: stop.lat,
          lng: stop.lng,
          label: stop.label,
          fillColor: getStopFillColor(type, status),
        } as LeafletMarkerModel;
      }),

      ...assignedPickups.map((pickup) => ({
        id: pickup.pickupNumber.toString(),
        kind: "pickup" as const,
        lat: pickup.shipper.address.geolocation.lat,
        lng: pickup.shipper.address.geolocation.lng,
        label: "PU",
        fillColor: getStopFillColor(
          StopType.PICKUP,
          PickupStatus[pickup.pickupStatus]
        ),
      } as LeafletMarkerModel)),

    ], [stops, assignedPickups, stopStatuses]);

    const polylines: LeafletPolylineModel[] = useMemo(() => {
      if (!polyline) return [];
      const decoded = decodeGooglePolylineToLatLng(polyline);
      return decoded.length > 1 ? [{
        id: `manifest-route-${manifestNumber}`,
        positions: decoded,
        color: "#23356d",
        weight: 5,
        opacity: 0.88,
      }] : [];
    }, [polyline, manifestNumber]);

    const renderPopup = (id: string, kind: LeafletMarkerKind) => {
      const stop = stops.find((s: StopMarker) => s.id === id);
      if (stop) {
        const info = stopsInfo?.[stop.label];

        if (info) {
          return (
            <XGSMarkerTooltip>
              <StopMarkerInfo info={info} /> 
            </XGSMarkerTooltip>
          );
        }

        if (!info && stop.description) {
          return (
            <XGSMarkerTooltip>
              <div dangerouslySetInnerHTML={{ __html: stop.description }} />
            </XGSMarkerTooltip>
          );
        }

        return null;
      }

      const pickup = assignedPickups.find(
        p => p.pickupNumber.toString() === id
      );
      if (pickup) {
        return (
          <XGSMarkerTooltip>
            <StopMarkerInfo
              info={{
                address: formatAddress(pickup.shipper.address),
                consignees: pickup.consignee.name,
              }}
            />
          </XGSMarkerTooltip>
        );
      }

      return null;
    };

    return (
      <div>
        <MapHeader
          title="Map of the Route"
          legend={<StopsLegend />}
        />

        <div className="xgs-manifest-map__container">
          <div className="xgs-manifest-map__state">
            {isError && <XGSErrorMessage>{manifestsState.map_fetch_fail_reason}</XGSErrorMessage>}
            {isLoading && <Loading isLoading={true} />}
          </div>
          <XGSLeafletMap
            markers={markers}
            polylines={polylines}
            renderPopup={renderPopup}
          />

        </div>
      </div>
    );
  };

  export default ManifestMap;
